/**
 * Background video for sections/apparel-image-banner.liquid.
 *
 * Plays only the video that matches the current breakpoint, only while the banner is on screen,
 * and never by itself for visitors who prefer reduced motion or when the merchant turns autoplay off.
 * The toggle button pauses and resumes it, and replays a video that doesn't loop once it has ended.
 *
 * Over an image, each video arrives as an inert <template data-banner-video-template> and is only turned
 * into a <video> the first time it should play, after that image (the LCP element) has loaded. The video
 * for the other breakpoint and any poster frame therefore never compete with the image for bandwidth.
 */
class ApparelBannerVideo extends HTMLElement {
  connectedCallback() {
    this.videos = [];
    this.templates = Array.from(this.querySelectorAll('template[data-banner-video-template]'));
    this.toggleButton = this.querySelector('[data-banner-video-toggle]');
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplay = this.hasAttribute('data-autoplay');
    this.isPausedByUser = !this.shouldAutoplay;
    this.isInView = false;
    this.pendingPoster = null;

    this.update = this.update.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
    this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
    this.onVideoReady = this.onVideoReady.bind(this);
    this.onVideoEnded = this.onVideoEnded.bind(this);

    this.querySelectorAll('[data-banner-video]').forEach((video) => this.setUpVideo(video));
    this.toggleButton?.addEventListener('click', this.onToggleClick);
    this.desktopQuery.addEventListener('change', this.update);
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);

    // This element is `display: contents` and has no box of its own, so watch the banner media it sits in.
    this.observer = new IntersectionObserver((entries) => {
      this.isInView = entries.some((entry) => entry.isIntersecting);
      this.update();
    });
    this.observer.observe(this.parentElement);

    this.updateToggle();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.pendingPoster = null;
    this.videos.forEach((video) => {
      video.removeEventListener('loadeddata', this.onVideoReady);
      video.removeEventListener('ended', this.onVideoEnded);
    });
    this.toggleButton?.removeEventListener('click', this.onToggleClick);
    this.desktopQuery.removeEventListener('change', this.update);
    this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
  }

  get breakpoint() {
    return this.desktopQuery.matches ? 'desktop' : 'mobile';
  }

  get shouldAutoplay() {
    return this.autoplay && !this.reducedMotionQuery.matches;
  }

  // The image shown at the current breakpoint; the video sits over it once it plays.
  get activePoster() {
    if (!this.hasAttribute('data-has-poster')) return null;
    return (
      this.querySelector(`.apparel-image-banner__image--${this.breakpoint}`) ||
      this.querySelector('.apparel-image-banner__image')
    );
  }

  setUpVideo(video) {
    video.muted = true;
    video.addEventListener('loadeddata', this.onVideoReady);
    video.addEventListener('ended', this.onVideoEnded);
    this.videos.push(video);
  }

  // Returns the video for a breakpoint, creating it from its template only when `create` is set.
  getVideo(breakpoint, create) {
    const video = this.videos.find((candidate) => candidate.dataset.breakpoint === breakpoint);
    if (video || !create) return video;

    const template = this.templates.find((candidate) => candidate.dataset.breakpoint === breakpoint);
    const templateVideo = template?.content.querySelector('[data-banner-video]');
    if (!templateVideo) return undefined;

    this.templates = this.templates.filter((candidate) => candidate !== template);
    template.replaceWith(templateVideo);
    this.setUpVideo(templateVideo);
    return templateVideo;
  }

  // Re-runs update() once the poster image settles; only one listener is kept, for the current poster.
  waitForPoster(poster) {
    if (this.pendingPoster === poster) return;
    this.pendingPoster = poster;

    const onSettled = () => {
      poster.removeEventListener('load', onSettled);
      poster.removeEventListener('error', onSettled);
      if (this.pendingPoster !== poster || !this.isConnected) return;
      this.pendingPoster = null;
      this.update();
    };
    poster.addEventListener('load', onSettled);
    poster.addEventListener('error', onSettled);
  }

  onVideoReady(event) {
    event.currentTarget.classList.add('is-ready');
  }

  // Only fires when Loop is off: stay on the last frame and offer Play, which restarts from the beginning.
  onVideoEnded() {
    this.isPausedByUser = true;
    this.updateToggle();
  }

  onToggleClick() {
    this.isPausedByUser = !this.isPausedByUser;
    this.update();
  }

  onReducedMotionChange() {
    this.isPausedByUser = !this.shouldAutoplay;
    this.update();
  }

  update() {
    const breakpoint = this.breakpoint;
    const shouldPlay = this.isInView && !this.isPausedByUser;

    this.videos.forEach((video) => {
      if (video.dataset.breakpoint !== breakpoint) video.pause();
    });

    // Let the image finish first: it is the largest paint, and the video only replaces it once playing.
    const poster = shouldPlay ? this.activePoster : null;
    if (poster && !poster.complete) {
      this.waitForPoster(poster);
      this.updateToggle();
      return;
    }

    const activeVideo = this.getVideo(breakpoint, shouldPlay);
    if (activeVideo) {
      if (shouldPlay) {
        activeVideo.play().catch((error) => {
          // Autoplay blocked (for example iOS Low Power Mode): show the play button so the visitor can start it.
          if (error.name !== 'NotAllowedError') return;
          this.isPausedByUser = true;
          this.updateToggle();
        });
      } else {
        activeVideo.pause();
        // With no image behind it, load the first frame so the banner isn't empty while paused.
        if (this.isInView && !this.hasAttribute('data-has-poster')) this.loadFirstFrame(activeVideo);
      }
    }

    this.updateToggle();
  }

  loadFirstFrame(video) {
    if (video.readyState > 0 || video.dataset.loadRequested) return;
    video.dataset.loadRequested = 'true';
    video.preload = 'auto';
    video.load();
  }

  updateToggle() {
    if (!this.toggleButton) return;
    const { playLabel, pauseLabel } = this.toggleButton.dataset;
    this.toggleButton.setAttribute('aria-label', this.isPausedByUser ? playLabel : pauseLabel);
    this.toggleButton.classList.toggle('is-paused', this.isPausedByUser);
  }
}

if (!customElements.get('apparel-banner-video')) {
  customElements.define('apparel-banner-video', ApparelBannerVideo);
}
